import { FormEvent, useState, useEffect } from "react";
import {
  AlignItems,
  Banner,
  BannerStatus,
  BannerVariant,
  Box,
  ButtonLayout,
  ButtonVariant,
  ColorPreset,
  ColorScheme,
  FieldSet,
  Form,
  FormFieldStatus,
  H2,
  Hint,
  Interpose,
  P,
  PlainLink,
  ReadingOptimized,
  Space,
  Tag,
  TagColor,
  TagSize,
  TagVariant,
  TypePreset,
  Visibility,
  BannerLeftAccessoryType,
  FontWeight,
  TypeScale,
} from "@gocardless/flux-react";
import { Trans } from "@lingui/macro";
import { FormProvider, useForm } from "react-hook-form";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import {
  CreditorType,
  VerificationMethod,
} from "@gocardless/api/dashboard/types";

import {
  SignupFormData,
  SignUpFormField,
  ValidationMode,
  WebsiteLinks,
} from "../consts";

import { EmailField } from "./form-fields/EmailField";
import { PasswordField } from "./form-fields/PasswordField";
import { BusinessNameField } from "./form-fields/BusinessNameField";
import { TermsAndConditionsCheck } from "./form-fields/TermsAndConditionsCheck/TermsAndConditionsCheck";
import { CreditorTypeDropdown } from "./form-fields/CreditorTypeDropdown";

import {
  ReferralEmailSource,
  ReferralEmailVariation,
} from "src/common/trackingEventAttributes";
import { CountryCodeSupported } from "src/common/country";
import { SectionSeparator } from "src/components/layout";
import { SeparatorType } from "src/components/layout/types";
import { useOptimizelyVariation } from "src/technical-integrations/optimizely/useOptimizelyVariation";
import { OptimizelyFlag } from "src/technical-integrations/optimizely/constants";
import { useSupportedCountries } from "src/libraries/creditor/hooks";
import { enumFromStringValue } from "src/utils/types";
import { getPaymentBuilderData } from "src/utils/getPaymentBuilderData";
import { CookieStorage } from "src/common/constants/storage";
import { SandboxTag } from "src/common/SandboxTag";
import {
  isCountryCodeSupported,
  isSandboxEnvironment,
} from "src/common/consts";
import { useSegment } from "src/technical-integrations/segment/useSegment";
import { TrackingEvent } from "src/common/trackingEvents";
import { useReferralAmountByGeo } from "src/components/routes/Refer/hooks/useReferralAmount";
import { useAccessToken } from "src/common/authorisation";
import { SubmitButton } from "src/components/shared/SubmitButton";

export const SIGNUP_FORM_STORAGE_KEY = "gc.signup_form_details";

export interface SignupProps {
  defaultCountryCode: string;
  isSubmittingEmailVerification: boolean;
  isSubmittingRegistration: boolean;
  onSubmit: (formData: SignupFormData) => void;
  defaultFormData?: SignupFormData;
  referralGeo: string | null;
  referralCode: string | null;
  referralSource?: ReferralEmailSource | null;
  referralVariation?: ReferralEmailVariation | null;
  referralCodeName?: string | null;
  hasFsbMemberDiscount?: boolean;
}

export const SignupForm = ({
  defaultCountryCode,
  isSubmittingEmailVerification,
  isSubmittingRegistration,
  onSubmit,
  defaultFormData,
  referralGeo,
  referralCode,
  referralSource,
  referralVariation,
  referralCodeName,
  hasFsbMemberDiscount,
}: SignupProps) => {
  const router = useRouter();
  const [accessToken] = useAccessToken();
  const referralAmount = useReferralAmountByGeo(referralGeo);
  const { sendEvent } = useSegment();
  const { isFlagEnabled: isVerificationsUnsupportedEntityType } =
    useOptimizelyVariation({
      flag: OptimizelyFlag.VERIFICATIONS_UNSUPPORTED_ENTITY_TYPE,
    });

  const unauthenticatedOptimizelyID = Cookies.get(
    CookieStorage.PreSignupOptimizelyID
  );

  const { isVariationOn: isExperienceProductV1 } = useOptimizelyVariation({
    flag: OptimizelyFlag.SPARK_EXPERIENCE_PRODUCT_V1,
    overrideUserId: unauthenticatedOptimizelyID,
  });

  const paymentBuilderData = getPaymentBuilderData();

  // https://gocardless.atlassian.net/browse/SPK-1139
  // Only greet the user if the cookie data exists and user is in variant
  const hasExperiencedTheProduct = !paymentBuilderData
    ? false
    : paymentBuilderData?.merchantName !== "" && isExperienceProductV1;

  const { isCreditorTypeSupported } = useSupportedCountries();

  const signUpFormHeadingText = isSandboxEnvironment ? (
    <Trans id="optimised-sign-up.form.heading-sandbox">
      Finish signing up for a free sandbox testing account
    </Trans>
  ) : hasFsbMemberDiscount ? (
    <Trans>Sign up today and get a 25% discount for 12 months</Trans>
  ) : hasExperiencedTheProduct ? (
    <>
      <P>Hi, {paymentBuilderData?.merchantName}</P>
      <P
        weight={FontWeight.Normal}
        size={[TypeScale.Size_03, null, TypeScale.Size_05]}
      >
        Finish signing up and get up to 90 days no fees
      </P>
    </>
  ) : (
    <Trans id="optimised-sign-up.form.heading">
      Sign up today for 90 days no fees
    </Trans>
  );

  const fsbDiscountTag = (
    <Tag
      variant={TagVariant.Solid}
      color={TagColor.Info}
      colorScheme={ColorScheme.OnDark}
      size={TagSize.Md}
    >
      <Trans>FSB member discount</Trans>
    </Tag>
  );
  const [checked, setChecked] = useState<boolean>(
    defaultFormData?.terms_and_conditions ?? false
  );

  const formMethods = useForm<SignupFormData>({
    defaultValues: {
      email: defaultFormData?.email ?? "",
      geo: defaultFormData?.geo ?? defaultCountryCode,
      password: defaultFormData?.password ?? "",
      business_name: hasExperiencedTheProduct
        ? paymentBuilderData?.merchantName ?? ""
        : defaultFormData?.business_name ?? "",
      terms_and_conditions: checked,
      verification_method: VerificationMethod.EmailVerification,
      creditor_type: defaultFormData?.creditor_type ?? undefined,
    },
    mode: ValidationMode.OnBlur,
    reValidateMode: ValidationMode.OnChange,
  });

  const { isValid, errors } = formMethods.formState;
  const { handleSubmit, watch, getValues } = formMethods;

  const hasReferralCode = !!(
    !isSandboxEnvironment &&
    referralCode &&
    referralGeo &&
    isCountryCodeSupported(referralGeo) &&
    referralGeo !== CountryCodeSupported.DE
  );

  // referral_code_name is ignored if referral_code is not also set
  const hasReferralCodeName = !!(hasReferralCode && referralCodeName);
  const isSignedInOnReferralPage = !!accessToken && hasReferralCode;

  const watchGeo = watch("geo");
  const fsbNotSupported = !!hasFsbMemberDiscount && watchGeo !== "GB";

  const creditorType = getValues(SignUpFormField.creditor_type) as CreditorType;
  const creditorGeo = getValues(SignUpFormField.geo) as CountryCodeSupported;

  const invalidCreditorType = (
    geoValue: CountryCodeSupported,
    creditorValue: CreditorType
  ) =>
    !isCreditorTypeSupported(
      enumFromStringValue(CountryCodeSupported, geoValue),
      enumFromStringValue(CreditorType, creditorValue)
    );

  const hasCreditorTypeError = (
    credGeo: CountryCodeSupported,
    credType: CreditorType
  ) => credGeo && credType && invalidCreditorType(credGeo, credType);

  useEffect(() => {
    const pageViewData = {
      business_location: defaultCountryCode,
      form_type: hasReferralCode ? "referral" : "self-serve",
      page_url: `${window.location.origin}${router.asPath}`,
      ...(hasReferralCode && {
        referral_source: referralSource,
        ...(referralVariation && { referral_variation: referralVariation }),
        ...(referralCodeName && { referrer: referralCodeName }),
        referralGeo: referralGeo,
      }),
    };
    sendEvent(TrackingEvent.SIGNUP_PAGE_VIEWED, pageViewData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Box>
        <ReadingOptimized alignText={AlignItems.FlexStart} textWidth="100%">
          {isSandboxEnvironment && (
            // TODO: This is a temp solution - once Optimized Signup form is fully productionised
            // we should be removing the double isSandboxEnvironment check
            // and move styles within SandboxTag
            <Box spaceBelow={1}>
              <SandboxTag />
            </Box>
          )}
          {!!hasFsbMemberDiscount && <Box spaceBelow={1}>{fsbDiscountTag}</Box>}
          <H2 spaceBelow={hasReferralCode ? 0.75 : [2, null, 3]} size={6}>
            {hasReferralCode ? (
              hasReferralCodeName ? (
                <Trans id="refer.sign-up.referral-code-name-heading">
                  {referralCodeName} invited you to join GoCardless with a{" "}
                  {referralAmount.incentive} reward
                </Trans>
              ) : (
                <Trans id="refer.sign-up.referral-code-heading">
                  Join GoCardless with a {referralAmount.incentive} reward
                </Trans>
              )
            ) : (
              signUpFormHeadingText
            )}
          </H2>
          {hasReferralCode && (
            <>
              <P preset={TypePreset.Body_01} color={ColorPreset.TextOnLight_02}>
                <Trans id="refer.sign-up.intro">
                  There&apos;s no transaction fees for 90 days and we&apos;ll
                  also give you {referralAmount.incentive} as a reward for
                  collecting at least {referralAmount.threshold}.
                </Trans>
              </P>
              <Space v={2} />
            </>
          )}
          <FormProvider {...formMethods}>
            <Form
              onSubmit={(e: FormEvent<HTMLFormElement>) =>
                handleSubmit((formData) => onSubmit(formData))(e)
              }
              key="signup-form"
            >
              <Interpose node={<Space v={2} />}>
                <Interpose node={<Space v={1} />}>
                  <FieldSet>
                    <Box>
                      <BusinessNameField
                        userCountryCode={
                          defaultFormData?.geo ?? defaultCountryCode
                        }
                      />
                      {errors?.business_name && (
                        <>
                          <Space v={0.5} />
                          <Hint status={FormFieldStatus.Danger}>
                            {errors.business_name.message}
                          </Hint>
                        </>
                      )}
                    </Box>
                    {fsbNotSupported && (
                      <>
                        <Space v={0.5} />
                        <Banner
                          variant={BannerVariant.Light}
                          leftAccessory={{
                            type: BannerLeftAccessoryType.Status,
                            status: BannerStatus.Warning,
                          }}
                        >
                          <Trans>
                            Please note: the 25% discount for FSB members is
                            only available for organisations based in the UK
                          </Trans>
                        </Banner>
                      </>
                    )}
                  </FieldSet>
                  <CreditorTypeDropdown
                    key="creditor-dropdown"
                    isSubmitting={
                      isSubmittingEmailVerification || isSubmittingRegistration
                    }
                    hasCreditorTypeError={hasCreditorTypeError}
                  />
                  {isVerificationsUnsupportedEntityType &&
                  hasCreditorTypeError(creditorGeo, creditorType) ? (
                    <></>
                  ) : (
                    <Interpose node={<Space v={1} />}>
                      <EmailField key="email-field" />
                      <PasswordField key="password-field" />
                    </Interpose>
                  )}
                </Interpose>
                {isVerificationsUnsupportedEntityType &&
                hasCreditorTypeError(creditorGeo, creditorType) ? (
                  <></>
                ) : (
                  <Interpose node={<Space v={2} />}>
                    <TermsAndConditionsCheck
                      checked={checked}
                      setChecked={setChecked}
                      isReferralSignup={hasReferralCode}
                      key="terms-and-conditions-check"
                      hasFsbMemberDiscount={hasFsbMemberDiscount}
                    />
                    <SubmitButton
                      layout={[ButtonLayout.Full, null, ButtonLayout.Inline]}
                      variant={ButtonVariant.PrimaryOnLight}
                      disabled={
                        !isValid ||
                        !checked ||
                        isSubmittingEmailVerification ||
                        isSubmittingRegistration ||
                        isSignedInOnReferralPage
                      }
                      isSubmitting={
                        isSubmittingEmailVerification ||
                        isSubmittingRegistration
                      }
                      key="submit-button"
                    >
                      {hasReferralCode ? (
                        <Trans id="refer.sign-up.cta.get-started-free">
                          Get started free
                        </Trans>
                      ) : (
                        <Trans id="optimised-sign-up.submit-button">
                          Sign up
                        </Trans>
                      )}
                    </SubmitButton>
                  </Interpose>
                )}
              </Interpose>
            </Form>
          </FormProvider>
          <Box spaceAbove={[2, null, 4]}>
            <Visibility visible={["none", null, "block"]}>
              <SectionSeparator type={SeparatorType.DEFAULT} />
            </Visibility>
            <P
              preset={TypePreset.Caption_01}
              color={ColorPreset.TextOnLight_02}
            >
              <Trans id="FCA-text">
                GoCardless Ltd is authorised by the Financial Conduct Authority
                under the Payment Services Regulations 2017 (Ref 597190). For
                GoCardless SAS and Inc. click{" "}
                <PlainLink
                  layout="inline"
                  textDecoration="underline"
                  href={WebsiteLinks.REGULATORY_INFO}
                  target="_blank"
                  color={ColorPreset.BackgroundDark_01}
                  onClick={() =>
                    sendEvent(TrackingEvent.SIGNUP_REGULATORY_INFO_LINK_CLICKED)
                  }
                >
                  here
                </PlainLink>
                .
              </Trans>
            </P>
          </Box>
        </ReadingOptimized>
      </Box>
    </Box>
  );
};
